// insurance-offers.ts
import { Component, Input, ChangeDetectionStrategy, Output, EventEmitter, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
  selector: 'app-insurance-offers-widget',
  standalone: true,
  imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
  templateUrl: './insurance-offers.html',
  styleUrls: ['./insurance-offers.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceOffers implements AfterViewInit, OnDestroy {
  @Input() title = 'Insurance Offers';
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
  @Input() index = 0;
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;

  private resizeObserver?: ResizeObserver;
  private readonly BASE_HEIGHT = 260;

  constructor(private elementRef: ElementRef) { }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private setupResizeObserver(): void {
    // Observe the parent .widget container which is what gets resized
    const widgetContainer = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
    const insuranceWidget = this.elementRef.nativeElement.querySelector('.insurance-offers-widget') as HTMLElement;

    if (!widgetContainer || !insuranceWidget) {
      if (!this.isAddMode) {
        console.warn('Insurance widget: container or widget element not found');
      }
      return;
    }

    // Initial update
    setTimeout(() => {
      this.applyDynamicScaling(insuranceWidget, widgetContainer.offsetHeight);
    }, 0);

    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (!this.resizeObserver) return;

        const isDragging = document.body.classList.contains('is-resizing');
        const resizingWidgetType = document.body.dataset['resizingWidget'];

        // Update when THIS widget is being resized OR when not dragging (size dropdown change)
        if ((isDragging && resizingWidgetType === 'insurance') || !isDragging) {
          const height = widgetContainer.offsetHeight;

          this.applyDynamicScaling(insuranceWidget, height);
        }
      });
    });

    this.resizeObserver.observe(widgetContainer);
  }

  private applyDynamicScaling(element: HTMLElement, height: number): void {
    if (height > this.BASE_HEIGHT) {
      // Calculate scale factor based on height increase
      const scaleFactor = 1 + ((height - this.BASE_HEIGHT) / this.BASE_HEIGHT) * 0.5;



      // Apply scaling via CSS custom properties
      element.style.setProperty('--logo-scale', scaleFactor.toString());
      element.style.setProperty('--font-scale', scaleFactor.toString());
    } else {

      // Reset to default when height is 260px or less
      element.style.setProperty('--logo-scale', '1');
      element.style.setProperty('--font-scale', '1');
    }
  }
}